import { useCallback, useEffect, useState } from 'react';
import { CAST_NAMESPACE } from './useGoogleCast.js';
import { presentUrlFromMessage } from '../routes/receiverModel.js';

/*
 * The receiver half of native cast: this hook runs ON the cast device. It
 * loads the CAF receiver framework, starts the receiver app with the custom
 * namespace, and surfaces the URL each sender hands over. Only the slice of
 * the CAF receiver API we touch is declared here — it is not in lib.dom.
 */
const RECEIVER_SDK =
  'https://www.gstatic.com/cast/sdk/libs/caf_receiver/v3/cast_receiver_framework.js';
const RECEIVER_SDK_ID = 'cast-receiver-sdk';

interface CastReceiverContextLike {
  start(options: Record<string, unknown>): void;
  addCustomMessageListener(namespace: string, listener: (message: { data?: unknown }) => void): void;
  setApplicationState(text: string): void;
}

type GlobalWithCast = Window & {
  cast?: {
    framework?: {
      CastReceiverContext?: { getInstance(): CastReceiverContextLike };
    };
  };
};

const receiverContext = (): CastReceiverContextLike | null =>
  (window as GlobalWithCast).cast?.framework?.CastReceiverContext?.getInstance() ?? null;

let startPromise: Promise<CastReceiverContextLike | null> | null = null;

/**
 * Loads the receiver framework and starts the receiver app once per page,
 * resolving with its context. Unlike the sender loader, the receiver framework
 * never calls `__onGCastApiAvailable` — `cast.framework` is simply there once
 * the script has run — so the app starts on the script's load event, or at
 * once when the framework is already on the page (which is how tests stub
 * it). A failed start resolves null — the page then just sits on its waiting
 * screen, which is also exactly what a plain browser tab does.
 */
function loadReceiverSdk(): Promise<CastReceiverContextLike | null> {
  if (startPromise) return startPromise;
  startPromise = new Promise((resolve) => {
    let settled = false;
    const done = (ctx: CastReceiverContextLike | null) => {
      if (!settled) {
        settled = true;
        resolve(ctx);
      }
    };
    const startApp = () => {
      const ctx = receiverContext();
      if (!ctx) {
        done(null);
        return;
      }
      try {
        ctx.start({
          customNamespaces: { [CAST_NAMESPACE]: 'JSON' },
          // The table keeps running on the TV after the phone that cast it
          // locks or walks away; there is no media for CAF to call "active".
          disableIdleTimeout: true,
        });
        ctx.setApplicationState('TV Casino — waiting for the table');
        done(ctx);
      } catch {
        done(null);
      }
    };
    if (receiverContext()) {
      startApp();
      return;
    }
    if (document.getElementById(RECEIVER_SDK_ID)) return; // already loading; its onload decides
    const script = document.createElement('script');
    script.id = RECEIVER_SDK_ID;
    script.src = RECEIVER_SDK;
    script.async = true;
    script.onload = startApp;
    script.onerror = () => done(null);
    document.head.appendChild(script);
  });
  return startPromise;
}

/** What the waiting screen knows: whether cast is live, and the handed URL. */
export interface CastReceiverHandle {
  castReady: boolean;
  presentUrl: string | null;
}

/**
 * Listens for the sender's present message. The namespace listener is
 * registered per mount (the once-per-page app start is a separate singleton),
 * so the lifetime of the subscription follows the component's.
 */
export function useCastReceiver(): CastReceiverHandle {
  const [castReady, setCastReady] = useState(false);
  const [presentUrl, setPresentUrl] = useState<string | null>(null);

  const onMessage = useCallback((data: unknown) => {
    setPresentUrl(presentUrlFromMessage(data));
  }, []);

  useEffect(() => {
    if (!window.isSecureContext) return;
    let live = true;
    void loadReceiverSdk().then((ctx) => {
      if (!live || !ctx) return;
      ctx.addCustomMessageListener(CAST_NAMESPACE, (message) => onMessage(message.data));
      setCastReady(true);
    });
    return () => {
      live = false;
    };
  }, [onMessage]);

  return { castReady, presentUrl };
}

/** The cast device's idle banner; a no-op wherever the SDK never started. */
export function setReceiverApplicationState(text: string): void {
  try {
    receiverContext()?.setApplicationState(text);
  } catch {
    /* the banner is cosmetic */
  }
}
